import React, { useEffect, useState, useContext } from "react";
import { formatDateOnly } from "helpers/dateOnly";
import { Box, Flex, Link } from "theme-ui";
import { useNavigate } from "react-router-dom";
import * as DashboardAPI from "api/Dashboard";
import { acceptOrDeny } from "api/PurchaseOrder";
import { createNote } from "api/Notes";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import { SubmitIcon, CheckMarkIcon, TimesIcon, QuestionMarkIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";
import BasicModal from "components/ui/BasicModal";
import UserContext from "context/User";
import { trackActivity } from "api/ActivityTracker";

const COMPONENT_HEIGHT = 500;

interface PendingPO {
  id: string;
  poNumber: string;
  projectName: string;
  amount: number;
  quantity: number;
  unit: string;
  serviceDate: string;
  sentDate: string;
  daysPending: number;
  urgency: "critical" | "warning" | "normal";
}

interface PORowProps {
  po: PendingPO;
  onAccept: (poId: string) => void;
  onReview: (poId: string) => void;
  onDeny: (poId: string) => void;
}

const PORow: React.FC<PORowProps> = ({ po, onAccept, onReview, onDeny }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const urgencyBadgeColor = po.urgency === "critical" ? themevals.colors.danger : po.urgency === "warning" ? themevals.colors.warning : themevals.colors.success;
  const urgencyBgColor = po.urgency === "critical" ? "#FDF3F3" : po.urgency === "warning" ? "#FEF3E0" : "#DDE8D2";
  const urgencyLabel = po.daysPending === 0 ? "Sent today" : po.daysPending === 1 ? "Sent yesterday" : `${po.daysPending} days pending`;
  const borderColor = po.urgency === "critical" ? themevals.colors.danger : po.urgency === "warning" ? themevals.colors.warning : themevals.colors.success;

  return (
    <Box
      sx={{
        padding: "20px",
        border: `1px solid ${themevals.colors.inputOutline}`,
        borderLeft: `8px solid ${borderColor}`,
        borderRadius: "6px",
        marginBottom: "12px",
        position: "relative",
        "&:last-child": {
          marginBottom: 0,
        },
      }}
    >
      <Flex sx={{ gap: "24px", alignItems: "flex-start" }}>
        {/* Left side - PO details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Header Row - PO Number and Badge */}
          <Flex sx={{ alignItems: "center", gap: "12px", marginBottom: "20px" }}>
            <Link
              href={`/purchase-orders/${po.id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                fontFamily: "Poppins",
                fontSize: "14px",
                fontWeight: 600,
                color: "black",
                textDecoration: "underline",
                cursor: "pointer",
                "&:hover": { 
                  color: "black"
                } 
              }}
            >
              {po.poNumber}
            </Link>
            <Box
              sx={{
                display: "inline-block",
                backgroundColor: urgencyBgColor,
                color: urgencyBadgeColor,
                padding: "4px 10px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: 500,
                fontFamily: "Poppins",
              }}
            >
              {urgencyLabel}
            </Box>
          </Flex>

          {/* Project Name */}
          <P fontFamily="Poppins" fontSize="14px" color="black" marginTop="0" marginBottom="4px" fontWeight="600">
            {po.projectName}
          </P>

          {/* Details Row */}
          <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.dkgrey} marginTop="0" marginBottom="0">
            Service Date: {formatDateOnly(po.serviceDate)} • {po.quantity} {po.unit} • Sent {formatDate(po.sentDate)}
          </P>
        </Box>

        {/* Right side - Amount and Action Buttons */}
        <Box sx={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "12px" }}>
          {/* Amount */}
          <P fontFamily="Poppins" fontSize="18px" fontWeight="600" color="black" marginTop="0" marginBottom="20px">
            {formatCurrency(po.amount)}
          </P>

          {/* Action Buttons */}
          <Flex sx={{ gap: "15px" }}>
            <BasicButton
              scheme="primary"
              onClick={() => onAccept(po.id)}
              styles={{ padding: "6px 16px", fontSize: "13px" }}
            >
              <SubmitIcon fill="#fff" direction="up" />
              Accept
            </BasicButton>
            <BasicButton
              scheme="secondary"
              outline={true}
              onClick={() => onReview(po.id)}
              styles={{ padding: "6px 16px", fontSize: "13px" }}
            >
              Review
            </BasicButton>
            <BasicButton
              scheme="dangerSecondary"
              onClick={() => onDeny(po.id)}
              styles={{ padding: "6px 16px", fontSize: "13px" }}
            >
              Deny
            </BasicButton>
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

const POsAwaitingResponse: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useContext<any>(UserContext);
  const [pos, setPOs] = useState<PendingPO[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmAcceptModalOpen, setConfirmAcceptModalOpen] = useState<boolean>(false);
  const [acceptNoteModalOpen, setAcceptNoteModalOpen] = useState<boolean>(false);
  const [confirmDenyModalOpen, setConfirmDenyModalOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [selectedPOId, setSelectedPOId] = useState<string | null>(null);
  const [acceptNote, setAcceptNote] = useState<string>("");
  const [denyNote, setDenyNote] = useState<string>("");

  const fetchPOs = async () => {
    try {
      setLoading(true);
      const data = await DashboardAPI.getProviderPendingPOs(5);
      if (data) {
        setPOs(data.purchaseOrders);
        setTotalCount(data.totalCount);
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching pending POs:", err);
      setError("Failed to load purchase orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPOs();
  }, []);

  const handleViewAll = () => {
    navigate("/purchase-orders");
  };

  const handleAccept = (poId: string) => {
    setSelectedPOId(poId);
    setConfirmAcceptModalOpen(true);
  };

  const handleReview = (poId: string) => {
    navigate(`/purchase-orders/${poId}`);
  };

  const handleDeny = (poId: string) => {
    setSelectedPOId(poId);
    setConfirmDenyModalOpen(true);
  };

  const handleAcceptConfirm = async () => {
    if (!selectedPOId) return;
    
    // Accept the PO immediately
    setSubmitting(true);
    setConfirmAcceptModalOpen(false);
    
    try {
      const res = await acceptOrDeny({ data: { id: selectedPOId }, update: { status: 2 } });
      
      if (res.po) {
        void trackActivity({
          module: "purchase_orders",
          action: "accept_purchase_order",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "purchase_order",
          entityId: res.po.id,
          metadata: {},
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        
        await fetchPOs();
        
        // Show the note modal after accepting
        setSubmitting(false);
        setAcceptNoteModalOpen(true);
      }
    } catch (error) {
      console.error("Error accepting PO:", error);
      toast.add("Failed to accept purchase order.", "var(--theme-ui-colors-danger)");
      setSubmitting(false);
      setSelectedPOId(null);
    }
  };

  const handleCancelAcceptNote = () => {
    setAcceptNoteModalOpen(false);
    setAcceptNote("");
    setSelectedPOId(null);
    toast.add("PO accepted.", "var(--theme-ui-colors-success)");
  };

  const handleSaveAcceptNote = async () => {
    if (!selectedPOId) return;
    
    setSubmitting(true);
    
    try {
      // Save note if provided
      if (acceptNote && acceptNote.trim()) {
        const noteData = {
          note: acceptNote.trim(),
          entityId: selectedPOId,
          entityType: "PurchaseOrder",
          private: false,
        };
        await createNote({ data: noteData });
      }
      
      setAcceptNote("");
      setAcceptNoteModalOpen(false);
      setSelectedPOId(null);
      toast.add("PO accepted.", "var(--theme-ui-colors-success)");
    } catch (error) {
      console.error("Error saving note:", error);
      toast.add("PO accepted, but failed to save note.", "var(--theme-ui-colors-warning)");
      setAcceptNoteModalOpen(false);
      setSelectedPOId(null);
    } finally {
      setSubmitting(false);
    }
  };

  const submitDeny = async () => {
    if (!selectedPOId) return;
    
    setSubmitting(true);
    
    try {
      // Deny the PO
      const res = await acceptOrDeny({ data: { id: selectedPOId }, update: { status: 4 } });
      
      if (res.po) {
        // If there's a note, create it
        if (denyNote && denyNote.trim()) {
          const noteData = {
            note: denyNote.trim(),
            entityId: selectedPOId,
            entityType: "PurchaseOrder",
            private: false,
          };
          await createNote({ data: noteData });
        }
        
        toast.add("PO denied.", "var(--theme-ui-colors-danger)");
        void trackActivity({
          module: "purchase_orders",
          action: "deny_purchase_order",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "purchase_order",
          entityId: res.po.id,
          metadata: { note: denyNote },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
        setDenyNote("");
        
        await fetchPOs();
      }
    } catch (err) {
      console.error("Error denying PO:", err);
      toast.add("Failed to deny purchase order.", "var(--theme-ui-colors-danger)");
    } finally {
      setSubmitting(false);
      setConfirmDenyModalOpen(false);
      setSelectedPOId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ padding: "24px" }}>
        <P fontFamily="Poppins" fontSize="14px" color="#6B7280">
          Loading purchase orders...
        </P>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: "24px" }}>
        <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.danger}>
          {error}
        </P>
      </Box>
    );
  }

  return (
    <>
    <Box
      sx={{
        backgroundColor: "white",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        borderRadius: "8px",
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      {/* Header */}
      <Flex
        sx={{
          padding: "16px 24px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          minHeight: "76px",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <P
          fontFamily="Poppins"
          fontSize="14px"
          fontWeight="600"
          color="black"
          marginTop="0"
          marginBottom="0"
        >
          Purchase Orders Awaiting Response ({totalCount})
        </P>
        {totalCount > 5 && (
          <BasicButton
            scheme="secondary"
            outline={true}
            onClick={handleViewAll}
          >
            View All
          </BasicButton>
        )}
      </Flex>

      {/* Content */}
      <Box sx={{ padding: "16px", flex: 1, overflowY: "auto" }}>
        {pos.length === 0 ? (
          <Box
            sx={{
              padding: "32px",
              textAlign: "center",
            }}
          >
            <P fontFamily="Poppins" fontSize="14px" color={themevals.colors.dkgrey} marginTop="0" marginBottom="0">
              No pending purchase orders
            </P>
          </Box>
        ) : (
          pos.map((po) => (
            <PORow
              key={po.id}
              po={po}
              onAccept={handleAccept}
              onReview={handleReview}
              onDeny={handleDeny}
            />
          ))
        )}
      </Box>
    </Box>
    {confirmDenyModalOpen && (
      <BasicModal
        title="Deny PO?"
        cancel_click={() => {
          setConfirmDenyModalOpen(false);
          setDenyNote("");
        }}
        cancel_text="No, cancel"
        $submitting={submitting}
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton
            type="button"
            scheme="danger"
            onClick={() => submitDeny()}
            $submitting={submitting}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{ margin: "0 0.625rem .25rem 0" }}
          >
            {!submitting && <TimesIcon fill={"#fff"} />}
            {"Deny PO"}
          </BasicButton>
        }
      >
        <P lineHeight={"22px"}>Are you sure you want to deny this PO?</P>
        <Box sx={{ marginTop: "20px" }}>
          <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginBottom="8px" marginTop="0" textAlign="left">
            Reason for denial <Box as="span" sx={{ fontWeight: 500 }}>(optional)</Box>
          </P>
          <textarea
            value={denyNote}
            onChange={(e) => setDenyNote(e.target.value)}
            rows={4}
            style={{
              width: "100%",
              padding: "12px",
              fontFamily: "Poppins",
              fontSize: "14px",
              border: `1px solid ${themevals.colors.inputOutline}`,
              borderRadius: "6px",
              resize: "none" as const,
            }}
          />
        </Box>
      </BasicModal>
    )}
    {confirmAcceptModalOpen && (
      <BasicModal
        title="Accept PO?"
        cancel_click={() => {
          setConfirmAcceptModalOpen(false);
          setAcceptNote("");
        }}
        cancel_text="No, cancel"
        $submitting={submitting}
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton
            type="button"
            scheme="primary"
            onClick={() => handleAcceptConfirm()}
            $submitting={submitting}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{ margin: "0 0.625rem .25rem 0" }}
          >
            {!submitting && <CheckMarkIcon fill={"#fff"} />}
            {"Accept PO"}
          </BasicButton>
        }
      >
        <P lineHeight={"22px"}>Are you sure you want to accept this PO?</P>
      </BasicModal>
    )}
    {acceptNoteModalOpen && (
      <BasicModal
        cancel_click={() => handleCancelAcceptNote()}
        cancel_text="Skip"
        $submitting={submitting}
        size={"sm"}
        actions={
          <BasicButton
            type="button"
            scheme="primary"
            onClick={() => handleSaveAcceptNote()}
            $submitting={submitting}
            $iconHeight="14px"
            $iconWidth="14px"
            $loadingColor="#fff"
            keepText={true}
            styles={{ margin: "0" }}
          >
            {"Save"}
          </BasicButton>
        }
      >
        <Box>
          <Flex sx={{ alignItems: "center", justifyContent: "center", gap: "8px", marginBottom: "20px" }}>
            <Box
              sx={{
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: themevals.colors.success,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CheckMarkIcon fill="#fff" width="12px" height="12px" />
            </Box>
            <P 
              fontFamily="Rubik" 
              fontSize="16px" 
              fontWeight="600" 
              marginBottom="0" 
              marginTop="0"
              color={themevals.colors.success}
            >
              PO Accepted
            </P>
          </Flex>
          <P fontFamily="Poppins" fontSize="14px" marginBottom="20px" marginTop="0" color={themevals.colors.dkgrey}>
            Leave a note with any additional comments.
          </P>
          <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginBottom="8px" marginTop="0" textAlign="left">
            Note <Box as="span" sx={{ fontWeight: 500 }}>(optional)</Box>
          </P>
          <textarea
            value={acceptNote}
            onChange={(e) => setAcceptNote(e.target.value)}
            rows={4}
            style={{
              width: "100%",
              padding: "12px",
              fontFamily: "Poppins",
              fontSize: "14px",
              border: `1px solid ${themevals.colors.inputOutline}`,
              borderRadius: "6px",
              resize: "none" as const,
            }}
          />
        </Box>
      </BasicModal>
    )}
  </>
  );
};

export default POsAwaitingResponse;
